import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
// primereact
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
// components
import Input from "../../components/input/Input";
import Panel from "../../components/panel/panel";
import CardProfile from "../../components/upload/CardProfile";
import MaskInput from "../../components/input/MaskInput";
import UserDocuments from "./UserDocuments";
import ModalEditPassword from "./ModalEditPassword";
// services
import { getProfile } from "../../services/Profile/getProfile";
import { patchProfile } from "../../services/Profile/patchProfile";
import { patchProfilePhoto } from "../../services/Profile/patchProfilePhoto";
import { getFormClientConfig } from "../../services/GeneralRegistration/getFormClientConfig";
// context
import { useAppContext } from "../../context/AppContext";
// utils
import { queryClient } from "../../utils/queryClient";
import { Address } from "../../components/modal/ModalRegister/content/Address";

const MyAccount = () => {
  const { handleSetAppContentClass, setProfilePhoto } = useAppContext();
  const form = useForm();
  const addressRef = useRef(null);

  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [visible, setVisible] = useState(false);

  const {
    data: profile,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["myProfile"],
    queryFn: () => getProfile(),
  });

  const { mutateAsync: patchProfileFn } = useMutation({
    mutationFn: patchProfile,
    onSuccess(_, variables) {
      queryClient.setQueryData([`myProfile`], (data) => {
        const updatedData = data;
        updatedData.email = variables.email;
        updatedData.contacts = variables.contacts;
        updatedData.address = variables.address;
        updatedData.desc = variables.desc;
        updatedData.highlighted = variables.highlighted;
        return updatedData;
      });
    },
    onError(error) {
      // console.warn(error);
    },
  });

  const { mutateAsync: patchProfilePhotoFn } = useMutation({
    mutationFn: patchProfilePhoto,
    onSuccess(res, _) {
      queryClient.setQueryData(["myProfile"], (data) => {
        const updatedData = data;
        localStorage.setItem("@profilePhoto", res.path);
        setProfilePhoto(res.path);

        const documentIndex = updatedData.documents.findIndex(
          (doc) => doc.type_file === "cadastro_geral_profile_photo"
        );

        if (documentIndex !== -1) {
          updatedData.documents[documentIndex] = {
            ...updatedData.documents[documentIndex],
            path: res.path,
          };
        } else {
          updatedData.documents.push({
            name: "Foto de perfil",
            path: res.path,
            type_file: "cadastro_geral_profile_photo",
            desc: null,
            folder_id: null,
          });
        }
        return updatedData;
      });
    },
    onError(error) {
      // console.warn(error);
    },
  });

  const handleInputRequired = async () => {
    const response = await getFormClientConfig();
    if (Array.isArray(response)) {
      localStorage.setItem("registerGeneral", response);
    }
  };

  const handlePhotoUpload = async (data) => {
    const notification = toast.loading("Por favor aguarde...");
    const submitData = {
      profile_photo: data.setFile,
      type: "cadastro_geral_profile_photo",
    };
    setFile(data.setFile);
    try {
      await patchProfilePhotoFn(submitData);
      toast.update(notification, {
        render: "Foto de perfil editada com sucesso!",
        autoClose: 1000,
        theme: "colored",
        type: "success",
        isLoading: false,
      });
      setLoading(false);
    } catch (e) {
      toast.update(notification, {
        render: "Houve um erro ao atualizar os dados, tente novamente",
        type: "error",
        theme: "colored",
        autoClose: 5000,
        isLoading: false,
      });
      setLoading(false);
    }
  };

  const handleAddressSubmit = async (addressData) => {
    setLoading(true);
    const notification = toast.loading("Por favor aguarde...");
    try {
      const formValues = form.getValues();
      const updatedContacts = [...contacts];
      const phoneIndex = updatedContacts.findIndex(
        (contact) => contact.type_number === 1
      );
      if (phoneIndex !== -1) {
        updatedContacts[phoneIndex] = {
          ...updatedContacts[phoneIndex],
          number: formValues.number,
        };
      } else {
        updatedContacts.push({ type_number: 1, number: formValues.number });
      }
      const submitData = {
        name: name,
        email: formValues.email,
        doc: profile.doc,
        type_doc: profile.type_doc,
        contacts: updatedContacts,
        address: {
          cep: addressData.cep,
          num: addressData.num,
          complement: addressData.complement || "",
          state: addressData.state,
          neighborhood: addressData.neighborhood,
          city: addressData.city,
          address: addressData.address,
        },
      };

      await patchProfileFn(submitData);

      toast.update(notification, {
        render: "Dados salvos com sucesso!",
        autoClose: 1000,
        theme: "colored",
        type: "success",
        isLoading: false,
      });
      setLoading(false);
    } catch (e) {
      toast.update(notification, {
        render: "Houve um erro ao salvar os dados, tente novamente",
        type: "error",
        theme: "colored",
        autoClose: 5000,
        isLoading: false,
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    handleInputRequired();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleSetAppContentClass("p-0");
    return () => {
      handleSetAppContentClass("");
      if (addressRef.current?.destroy) {
        addressRef.current.destroy();
      }
    };
  }, [handleSetAppContentClass]);

  useEffect(() => {
    if (profile) {
      const profilePicture = profile.documents.find(
        (item) => item.type_file === "cadastro_geral_profile_photo"
      );
      if (profilePicture !== undefined) {
        setFile(profilePicture.path);
      }
      setContacts(profile.contacts);
      const phone = profile.contacts.filter((n) => n.type_number === 1);
      setName(profile.name);
      setRole(profile.role.name);
      setDocuments(profile.documents);

      form.setValue("name", profile.name);
      form.setValue("email", profile.email);
      form.setValue("number", phone.length > 0 ? phone[0].number : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  return (
    <>
      <div className="profile">
        <div className="profile-header">
          <div className="profile-header-cover"></div>
          <div className="profile-header-content">
            {isLoading ? (
              <div className="profile-header-img mb-4">
                <div style={{ translate: "-5px -10px" }}>
                  <Skeleton
                    width="155px"
                    height="155px"
                    className="rounded-circle"
                  />
                </div>
              </div>
            ) : (
              <div className="profile-header-img mb-4">
                <CardProfile
                  name="profile_photo"
                  control={form.control}
                  style={
                    window.innerWidth <= 767
                      ? { scale: "0.6", marginTop: "-40px" }
                      : {}
                  }
                  setValue={form.setValue}
                  preValue={file && file}
                  onPhotoUpload={handlePhotoUpload}
                />
              </div>
            )}
            <div
              className={`profile-header-info ${
                window.innerWidth <= 767 ? "" : "mt-5"
              }`}
            >
              <h4 className="mt-0 mb-1">{name ? name : "Carregando..."}</h4>
              <p className="mb-2">{role ? role : "Carregando..."}</p>
            </div>
          </div>
          <ul className="profile-header-tab nav nav-tabs w-100">
            <li className="nav-item">
              <a
                href="#profile-post"
                className="nav-link active"
                data-bs-toggle="tab"
              >
                Conta
              </a>
            </li>
            <li className="nav-item">
              <a
                href="#profile-about"
                className="nav-link"
                data-bs-toggle="tab"
              >
                Documentos
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="profile-content">
        <div className="tab-content p-0">
          <div className="tab-pane fade show active" id="profile-post">
            <div>
              {isLoading ? (
                <Skeleton width="100%" height={400} />
              ) : (
                <>
                  <Panel title="Informações do usuário">
                    <div className="row">
                      <div className="col-md-12 mt-2">
                        <div className="inline-flex align-items-center">
                          <i className="pi pi-user me-2"></i>
                          <b>Informações Pessoais</b>
                        </div>
                      </div>
                      <div className="col-xl-3 col-md-3 my-2">
                        <Input
                          isRequired={true}
                          name="email"
                          errors={form.formState.errors}
                          control={form.control}
                          label="E-mail"
                          placeholder="E-mail"
                        />
                      </div>
                      <div className="col-xl-3 col-md-3 my-2">
                        <MaskInput
                          isRequired={true}
                          control={form.control}
                          errors={form.formState.errors}
                          label="Celular"
                          mask="(99) 99999-9999"
                          name="number"
                        />
                      </div>
                      <div className="col-xl-3 col-md-6 my-2 pt-2">
                        <Button
                          rounded
                          type="button"
                          label="Alterar Senha"
                          icon="fa fa-right-left"
                          className="p-inputtext-sm w-100"
                          onClick={() => setVisible(true)}
                        />
                      </div>
                    </div>

                    <div className="row py-3 d-flex justify-content-center w-100">
                      <Address
                        ref={addressRef}
                        address={profile?.address}
                        onSubmitAddress={handleAddressSubmit}
                      />
                    </div>
                    <div className="row justify-content-end mt-4 pe-4">
                      <div className="col-md-1">
                        <Button
                          label="Salvar"
                          icon="fa fa-save"
                          iconPos="left"
                          className="w-100"
                          loading={loading}
                          onClick={() => {
                            addressRef.current?.submit();
                          }}
                        />
                      </div>
                    </div>
                  </Panel>
                </>
              )}
            </div>
          </div>
          <div className="tab-pane fade" id="profile-about">
            <UserDocuments
              documents={documents}
              setDocuments={setDocuments}
              refetch={refetch}
              isRefetching={isRefetching}
            />
          </div>
        </div>
      </div>
      <ModalEditPassword visible={visible} setVisible={setVisible} />
    </>
  );
};

export default MyAccount;
